import { useState, useEffect } from "react";
import StepCard from "./StepCard";
import QuestionForm from "./QuestionForm";
import { useNavigate } from "react-router-dom";

const USACompanyFormation = () => {
  const [steps, setSteps] = useState([
    { step: 1, title: "Company formation checklist", status: "in-progress", progress: 0, answers: {}, comments: {}, file: null },
    { step: 2, title: "Post Formation documents", status: "pending", progress: 0 },
    { step: 3, title: "Tax ID (EIN)", status: "pending", progress: 0 },
  ]);

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [assignedCompanyForm, setAssignedCompanyForm] = useState("LLC");
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Calculate total questions based on company type
  const getTotalQuestions = () => {
    if (assignedCompanyForm === "C-Corp") {
      // C-Corp: Basic Info (6) + President (3) + Secretary (3) + Treasurer (6) + Responsible Party (12) = 30
      return 30;
    } else {
      // LLC: Basic Info (6) + Partner 1 (3) + Partner 2 (3) + Partner 3 (3) + Responsible Party (11) = 26
      return 26;
    }
  };
  
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch user data");

        const data = await response.json();
        setUserData(data.user);
        
        // Initialize company form type from user data or default to LLC
        const companyForm = data.user.assignedCompanyForm || "LLC";
        setAssignedCompanyForm(companyForm);

        // Initialize steps
        if (data.user.companySteps && data.user.companySteps.length > 0) {
          setSteps(data.user.companySteps);
          
          // If we have step 1 data, make sure the company type matches
          const step1 = data.user.companySteps.find(s => s.step === 1);
          if (step1 && step1.answers && step1.answers.businessType) {
            setAssignedCompanyForm(step1.answers.businessType);
          }
        }
        
        setIsInitialized(true);
      } catch (error) {
        console.error(error);
        setError('Failed to load user data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleStepClick = (stepNumber) => {
    if (stepNumber === 1) {
      if (steps[0].status === "submitted") {
        alert("Step 1 has already been submitted and cannot be reopened.");
        return;
      }
      setIsFormVisible(true);
    }
  };

  const handleFormSubmit = async (data, isFinalSubmit) => {
    const answeredCount = Object.keys(data.answers).filter((k) => data.answers[k]).length;
    const token = localStorage.getItem("token");
    
    // Determine the new status based on whether this is a final submit
    const newStatus = isFinalSubmit ? "submitted" : "in-progress";
    
    // First, update the UI optimistically
    const updatedSteps = steps.map((s) => {
      if (s.step === 1) {
        return {
          ...s,
          status: newStatus,
          progress: answeredCount,
          answers: data.answers,
          comments: data.comments,
          file: data.file || s.file,
          error: false,
          errorMessage: ''
        };
      }
      // If this is a final submit, update step 2 to in-progress
      if (s.step === 2 && isFinalSubmit) {
        return {
          ...s,
          status: "in-progress"
        };
      }
      return s;
    });

    // Update the UI immediately
    setSteps(updatedSteps);
    setError(null);

    try {
      // Prepare the data in the format expected by the backend
      const requestData = {
        steps: updatedSteps.map(step => ({
          step: step.step,
          title: step.title,
          status: step.status,
          progress: step.progress || 0,
          answers: step.answers || {},
          comments: step.comments || {},
          file: step.file || null
        })),
        answers: data.answers || {},
        comments: data.comments || {},
        file: data.file || null
      };

      console.log('Sending update request:', requestData);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/usa-company-formation/update-steps`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      const responseData = await response.json().catch(() => ({}));
      console.log('Update response:', { status: response.status, responseData });

      if (!response.ok) {
        throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
      }

      // If we get here, the update was successful
      if (isFinalSubmit) {
        alert("Form submitted successfully!");
        setIsFormVisible(false);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      
      // Update steps with error state
      const errorSteps = steps.map((s) => {
        if (s.step === 1) {
          return {
            ...s,
            status: 'in-progress',
            error: true,
            errorMessage: error.message || 'Failed to save progress. Please try again.'
          };
        }
        return s;
      });
      
      setSteps(errorSteps);
      setError(error.message || 'Failed to save progress. Please try again.');
    }
  };

  // Don't render anything until we've finished initializing
  if (loading || !isInitialized) {
    return (
      <div className="usa-formation-tracker-container">
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading your USA Company Formation information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="usa-formation-tracker-container">
        <div className="text-center p-5">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="usa-formation-tracker-container">
      <div className="usa-formation-instruction-marquee">
        <marquee behavior="scroll" direction="left" scrollamount="8">
          🏢 USA Formation: Start with Company Formation Checklist ➡️ Post Formation Documents ➡️ Tax ID (EIN) | 
          💬 Use Chat with Us to communicate | 
          📝 Check Tax Filing for instructions
        </marquee>
      </div>

      <h2>USA Company Formation: {userData ? userData.name : ''}</h2>
      <div className="usa-formation-step-grid">
        {steps.map((s) => (
          <StepCard
            key={`usa-formation-${s.step}`}
            step={s.step}
            title={s.title}
            status={s.status}
            progress={s.progress}
            totalQuestions={s.step === 1 ? getTotalQuestions() : 1}
            error={s.error || false}
            errorMessage={s.errorMessage || ''}
            onClick={() => handleStepClick(s.step)}
          />
        ))}
      </div>

      {isFormVisible && (
        <QuestionForm
          onClose={() => setIsFormVisible(false)}
          onSubmit={(data, isFinal) => handleFormSubmit(data, isFinal)}
          initialAnswers={steps[0].answers}
          initialComments={steps[0].comments}
          isSubmitted={steps[0].status === "submitted"}
          companyType={assignedCompanyForm}
        />
      )}
    </div>
  );
};

export default USACompanyFormation;